import { ContentType } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { FullScreenIcon } from 'components/icon/FullScreenIcon';
import { ActionMenuItem } from 'components/menu/ActionMenuItem';
import { useNotificationContext } from 'contexts/NotificationContext';
import {
    fetchFileByPath,
    getFileObjectPath,
} from 'custom_hooks/data_hooks/file/useFileLibraryObject';
import { useTheme } from 'styled-components';
import { ThemeType } from 'styles/colorMode';
import { tokens } from 'styles/tokens';
import { contentTypeToUnknownMediaType, hasFullSize } from 'utils/file';
import { queryClient } from 'utils/query';
import type { FileActionsBase, FileIsh } from './base';

export type OpenFullSizeInput = {
    storageFileName: string | undefined;
    mediaType: string | undefined;
    entryType: ContentType | undefined;
};

function fetchFullSizeFile(colorMode: ThemeType, file: OpenFullSizeInput) {
    if (!file.storageFileName) {
        return Promise.resolve(null);
    }
    const path = getFileObjectPath(file.storageFileName);
    return queryClient.ensureQueryData({
        queryKey: ['picture', path, colorMode],
        queryFn: () =>
            fetchFileByPath(
                path,
                colorMode,
                file.mediaType ??
                    (file.entryType
                        ? contentTypeToUnknownMediaType(file.entryType)
                        : undefined) ??
                    'unknown',
            ),
        staleTime: Infinity,
    });
}

export function useOpenFullSize() {
    const { colorMode } = useTheme();
    const { showToast } = useNotificationContext();

    return useMutation({
        mutationFn: (file: OpenFullSizeInput) => fetchFullSizeFile(colorMode, file),
        onSuccess: (data) => {
            if (!data) {
                return;
            }
            const newWindow = window.open(data.blobUrl);
            if (!newWindow) {
                showToast({
                    type: 'error',
                    content:
                        'Unable to open file. Please allow popups for this site and try again.',
                });
            }
        },
        onError: () => {
            showToast({ type: 'error', content: 'Unable to load file.' });
        },
    });
}

function useOpenFullSizeMutation(base: FileActionsBase) {
    const { colorMode } = useTheme();

    return useMutation(
        base.actionMutationOptions({
            mutationFn: async (picture: FileIsh) =>
                fetchFullSizeFile(colorMode, {
                    storageFileName: picture.StorageFileName,
                    mediaType: picture.MediaType,
                    entryType: picture.EntryType,
                }),
            errorMessage: 'Unable to load file.',
            onSuccess: (data) => {
                if (data) {
                    window.open(data.blobUrl);
                }
            },
        }),
    );
}

function OpenFullSizeMenuItem({
    base,
    mutate,
}: {
    base: FileActionsBase;
    mutate: (file: FileIsh) => void;
}) {
    if (!hasFullSize(base.fileType)) {
        return null;
    }
    return (
        <ActionMenuItem
            title="View Full Size"
            hoverIcon={<FullScreenIcon />}
            icon={<FullScreenIcon color={tokens.text.secondary} />}
            onSelect={() => mutate(base.rowData)}
        />
    );
}

export const OpenFullSize = {
    useMutation: useOpenFullSizeMutation,
    MenuItem: OpenFullSizeMenuItem,
};
